import { ReactNode } from "react";
import classNames from "classnames";
import gql from "graphql-tag";
import { useParams } from "react-router-dom";

import { Background } from "components/Background/Backgound";
import { Icon } from "components/Icon/Icon";
import { Link } from "components/Link/Link";
import { Query } from "components/Query/Query";
import {
  FeedbackReportFragment,
  GetFeedbackReport,
} from "generated/copilot-api-developer";
import { useTranslation } from "i18n";

import { feedbackReportsListRoute } from "./feedbackReportsRoutes";
import { Rating } from "./Rating";
import { StatusBadge } from "./StatusBadge";

gql`
  # schema = COPILOT_API_DEVELOPER
  query GetFeedbackReport($uuid: UUID!) {
    feedbackReport(uuid: $uuid) {
      feedbackReport {
        ...FeedbackReport
      }
    }
  }
`;

export const FeedbackReportDetails = () => {
  const t = useTranslation();
  const { reportUuid } = useParams();
  if (!reportUuid) return null;

  return (
    <Background className="flex-col flex-fill overflow-auto p-16 lg:p-44 space-y-24">
      <Link
        to={feedbackReportsListRoute()}
        className="flex items-center text-16 font-medium text-primary-dark"
      >
        <Icon name="chevron" rotate={180} className="mr-10" size={24} />
        {t("developers.feedback_reports.back_to_list")}
      </Link>
      <Query query={GetFeedbackReport} variables={{ uuid: reportUuid }}>
        {(data) => (
          <FeedbackReportDetailsContent report={data.feedbackReport} />
        )}
      </Query>
    </Background>
  );
};

const FeedbackReportDetailsContent = ({
  report,
}: {
  report: FeedbackReportFragment;
}) => {
  const t = useTranslation();
  return (
    <section className="flex-col space-y-24">
      <dl className="grid grid-cols-2 gap-x-20 gap-y-16 rounded shadow-sm-outlined border bg-white w-full p-20">
        <DetailRow
          label={t("developers.feedback_reports.feedback_id")}
          value={report.feedbackId}
        />
        <DetailRow
          label={t("developers.feedback_reports.status")}
          value={<StatusBadge status={report.status} />}
        />
        <DetailRow
          label={t("developers.feedback_reports.external_user_id")}
          value={report.user?.externalId}
        />
        <DetailRow
          label={t("developers.feedback_reports.rating")}
          value={
            report.rating === null ? null : <Rating rating={report.rating} />
          }
        />
        <DetailRow
          label={t("developers.feedback_reports.reply_to")}
          value={report.replyTo}
        />
        <DetailRow
          label={t("developers.feedback_reports.specialty")}
          value={report.specialty}
        />
        <DetailRow
          label={t("developers.feedback_reports.locale")}
          value={report.locale}
        />
        <DetailRow
          label={t("developers.feedback_reports.template_key")}
          value={report.templateKey}
        />
        <DetailRow
          label={t("developers.feedback_reports.endpoint")}
          value={report.endpoint}
        />
        <DetailRow
          label={t("developers.feedback_reports.version")}
          value={report.version}
        />
        <DetailRow
          label={t("developers.feedback_reports.created_at")}
          value={report.createdAt.format("dateAndTime")}
        />
        <DetailRow
          label={t("developers.feedback_reports.updated_at")}
          value={report.updatedAt.format("dateAndTime")}
        />
        <DetailRow
          label={t("developers.feedback_reports.comment")}
          value={report.comment}
          className="col-span-2"
        />
      </dl>
    </section>
  );
};

const DetailRow = ({
  label,
  value,
  className,
}: {
  label: string;
  value: ReactNode;
  className?: string;
}) => (
  <div className={classNames("flex-col", className)}>
    <dt className="text-12 text-body uppercase">{label}</dt>
    <dd className="text-primary-dark text-14 whitespace-pre-wrap break-words">
      {value === null || value === undefined || value === "" ? "—" : value}
    </dd>
  </div>
);
